<script lang="ts">
    import { afterUpdate, createEventDispatcher } from "svelte";
    import { convertStringToEmote, isStringAnEmote } from "../misc/emote";
    import { convertNameToColour } from "../misc/nameToColour";
    import { name } from "../store/userdata";
    import { getColourClass } from "../styles/nameColourClasses";

    import type { ChatMessage } from "../types/chat";
    import type { EmoteType } from "../types/emoteType";

    import Emote from "./Emote.svelte";
    import EmotePicker from "./EmotePicker.svelte";

    let dispatch = createEventDispatcher<{
        submitMessage: string;
    }>();

    let messageBoxText: string = "";
    $: isMessagseBoxEmpty = messageBoxText === "";

    export let messages: ChatMessage[] = [];

    $: {
        if (messages.length > 100) {
            const halfMessages = messages.length / 2;
            messages = messages.slice(-halfMessages); // Halve messages
        }
    }

    function onMessageSubmit() {
        if (!messageBoxText || messageBoxText === "") return;

        dispatch("submitMessage", messageBoxText);

        const message: ChatMessage = {
            name: $name,
            message: messageBoxText,
        };
        messages = [...messages, message];
        messageBoxText = "";
    }

    function onEmoteSelected(selectedEmote) {
        messageBoxText = `:${selectedEmote.detail}:`;
        onMessageSubmit();
    }

    function convertMessageToCssColourClass(message: ChatMessage): string {
        const nameColour = convertNameToColour(message.name);
        return getColourClass(nameColour);
    }

    const isMessageAnEmote = (message: ChatMessage) => {
        if (!isSurroundedByColons(message.message)) return false;

        const unpack = unpackColons(message.message);
        return isStringAnEmote(unpack);
    };

    const convertMessageToEmote: (msg: ChatMessage) => EmoteType = (msg) => {
        const unpack = unpackColons(msg.message);
        const emote = convertStringToEmote(unpack);
        return emote;
    };

    function isSurroundedByColons(str: string) {
        const firstChar = str[0];
        const lastChar = str[str.length - 1];

        return firstChar === ":" && lastChar === ":";
    }

    function unpackColons(str: string) {
        const unpack = str.slice(1, str.length - 1);
        return unpack;
    }

    afterUpdate(() => {
        const chatbox = document.getElementById("chatbox-parent");
        if (chatbox) chatbox.scrollTop = chatbox.scrollHeight;
    });
</script>

<div id="chat">
    <div class="chat-head">
        <div>
            <p class="chat-label">Chat</p>
            <strong>Table banter</strong>
        </div>
    </div>
    <div id="chatbox-parent">
        <div id="chatbox">
            {#each messages as message}
                <div class="message-row">
                    <p
                        aria-label="message-name"
                        class={`inline bold message-name ${convertMessageToCssColourClass(message)}`}
                    >
                        {message.name}:
                    </p>
                    {#if isMessageAnEmote(message)}
                        <Emote emote={convertMessageToEmote(message)} />
                    {:else}
                        <p aria-label="message-contents" class="inline">
                            {message.message}
                        </p>
                    {/if}
                </div>
            {/each}
        </div>
    </div>
    <div class="composer">
        <input
            type="text"
            maxlength="500"
            aria-label="message-box"
            placeholder="Message"
            bind:value={messageBoxText}
            on:keypress={(e) => {
                const keycode = e.code || e.key;
                if (keycode === 'Enter') {
                    onMessageSubmit();
                }
            }}
        />
        <EmotePicker on:selectEmote={onEmoteSelected} />
        <button
            aria-label="submit-button"
            disabled={isMessagseBoxEmpty}
            on:click={(e) => onMessageSubmit()}
        >Submit</button>
    </div>
</div>

<style>
    #chat {
        border-radius: var(--radius-lg);
        border: 1px solid rgba(126, 173, 255, 0.16);
        background:
            linear-gradient(180deg, rgba(14, 28, 49, 0.98), rgba(7, 16, 30, 0.98));
        padding: 18px;
        box-shadow: var(--shadow-soft);
    }

    .chat-head {
        margin-bottom: 14px;
    }

    .chat-label {
        margin: 0 0 6px;
        color: var(--text-dim);
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.16em;
    }

    .chat-head strong {
        font-size: 1.05rem;
    }

    #chatbox-parent {
        height: 320px;
        overflow: auto;
        border-radius: var(--radius-md);
        border: 1px solid rgba(126, 173, 255, 0.12);
        background: rgba(7, 18, 33, 0.76);
        padding: 14px;
    }

    #chatbox {
        display: flex;
        justify-content: flex-end;
        flex-direction: column;
        min-height: 100%;
        gap: 10px;
    }

    .message-row {
        line-height: 1.5;
    }

    .message-name {
        margin-right: 0.35rem;
    }

    .composer {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto auto;
        gap: 10px;
        margin-top: 14px;
        align-items: center;
    }

    .inline {
        display: inline;
    }

    .bold {
        font-weight: bold;
    }

    @media (max-width: 640px) {
        .composer {
            grid-template-columns: 1fr;
        }
    }
</style>
